import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, LineChart, Line,
} from 'recharts';
import {
  Users, Award, AlertTriangle, Brain, UsersRound,
  HeartPulse, TrendingUp, ChevronRight, AlertCircle, List,
} from 'lucide-react';
import {
  LevelMetrics, CompetencyBucket, TopPerformer, AlertItem,
} from '../types';

// ========== Colour tokens ==========
const BUCKET_COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444'];
const LEVEL_COLOR_MAP: Record<string, string> = {
  Excellent:       '#10B981',
  Good:            '#6366F1',
  Average:         '#F59E0B',
  'Below Average': '#EF4444',
  Proficient:      '#10B981',
  Progressing:     '#F59E0B',
  Beginner:        '#EF4444',
};
const PIE_COLORS = ['#10B981', '#6366F1', '#F59E0B', '#EF4444'];

// ========== Sub-components ==========
/**
 * The four KPI cards. `layout="grid"` stacks them 2×2 and fills the container's
 * height — for the space beside the competency chart when a stage has no subject pie.
 */
export const MetricsRow: React.FC<{ m: LevelMetrics; layout?: 'row' | 'grid' }> = ({ m, layout = 'row' }) => {
  const grid = layout === 'grid';
  const cards = [
    { label: 'Total Students', value: m.totalStudents.toLocaleString(), icon: Users, color: 'bg-indigo-50 text-indigo-600', border: 'border-indigo-200' },
    { label: 'Avg Competency', value: `${m.avgCompetency}%`, icon: Award, color: 'bg-blue-50 text-blue-600', border: 'border-blue-200' },
    { label: 'Need Support', value: m.needsSupportCount.toLocaleString(), icon: AlertTriangle, color: 'bg-amber-50 text-amber-600', border: 'border-amber-200' },
    { label: 'Excellent Students', value: m.excellentCount.toLocaleString(), icon: TrendingUp, color: 'bg-purple-50 text-purple-600', border: 'border-purple-200' },
    // { label: 'Avg Behavior', value: `${m.avgBehaviorScore}`, icon: Brain, color: 'bg-pink-50 text-pink-600', border: 'border-pink-200' },
    // { label: 'Avg Peer Score', value: `${m.avgPeerScore}`, icon: UsersRound, color: 'bg-cyan-50 text-cyan-600', border: 'border-cyan-200' },
    // { label: 'BMI Normal', value: `${m.bmiNormalPercent}%`, icon: HeartPulse, color: 'bg-lime-50 text-lime-600', border: 'border-lime-200' },
  ];
  return (
    <div className={grid ? 'grid grid-cols-2 gap-4 h-full' : 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-6'}>
      {cards.map(c => (
        <div
          key={c.label}
          className={`bg-white rounded-xl border ${c.border} p-4 shadow-sm ${grid ? 'flex flex-col items-start justify-center' : ''}`}
        >
          <div className={`p-1.5 rounded-lg ${c.color} inline-flex mb-2`}><c.icon size={grid ? 18 : 16} /></div>
          <p className={`${grid ? 'text-2xl' : 'text-lg'} font-bold text-gray-900 leading-tight`}>{c.value}</p>
          <p className={`${grid ? 'text-xs' : 'text-[11px]'} text-gray-500 mt-0.5`}>{c.label}</p>
        </div>
      ))}
    </div>
  );
};

export const CompetencyChart: React.FC<{ buckets: CompetencyBucket[]; onBarClick?: (level: string) => void }> = ({ buckets, onBarClick }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">Competency Distribution</h3>
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={buckets} barSize={48}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis dataKey="level" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }} />
        <Bar dataKey="count" radius={[6, 6, 0, 0]} onClick={onBarClick ? (data: any) => onBarClick(data.level) : undefined} cursor={onBarClick ? 'pointer' : undefined}>
          {buckets.map((b, i) => <Cell key={i} fill={LEVEL_COLOR_MAP[b.level] ?? BUCKET_COLORS[i] ?? '#6366F1'} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export interface SubjectBucket {
  [key: string]: any;
  subject: string;
  avgScore: number;
}

const SUBJECT_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#3B82F6', '#EC4899', '#8B5CF6'];

const PREP_SUBJECTS = ['Hindi', 'English', 'Mathematics', 'EVS', 'Computer Science'];
const PREP_OFFSETS = [2, -3, 5, -1, 4];
const FOUND_SUBJECTS = ['Hindi', 'English', 'Mathematics', 'EVS'];
const FOUND_OFFSETS = [1, -2, 3, -1];

export function mkSubjectBuckets(
  avgCompetency: number,
  stage: 'preparatory' | 'foundation' = 'preparatory',
): SubjectBucket[] {
  const subjects = stage === 'preparatory' ? PREP_SUBJECTS : FOUND_SUBJECTS;
  const offsets = stage === 'preparatory' ? PREP_OFFSETS : FOUND_OFFSETS;
  return subjects.map((subject, i) => ({
    subject,
    avgScore: Math.round(Math.max(0, Math.min(100, avgCompetency + offsets[i]))),
  }));
}

interface SubjectBreakdownPieProps {
  data: SubjectBucket[];
  title?: string;
}

export const SubjectBreakdownPie: React.FC<SubjectBreakdownPieProps> = ({ data, title = 'Subject Breakdown' }) => {
  const getSubjectMeta = (subjectName: string) => {
    const nameLower = subjectName.toLowerCase();
    if (nameLower.includes('math') || nameLower.includes('ganit') || nameLower.includes('गणित') || nameLower.includes('numeracy')) {
      return { icon: '🔢', bgClass: 'bg-blue-500', barColor: '#3B82F6' };
    }
    if (nameLower.includes('l1') || nameLower.includes('fluency') || nameLower.includes('hindi') || nameLower.includes('साक्षरता') || nameLower.includes('sanskrit') || nameLower.includes('mother tongue') || nameLower.includes('regional')) {
      return { icon: '📖', bgClass: 'bg-amber-500', barColor: '#F59E0B' };
    }
    if (nameLower.includes('communication') || nameLower.includes('oral') || nameLower.includes('english') || nameLower.includes('language') || nameLower.includes('literacy')) {
      return { icon: '🗣️', bgClass: 'bg-orange-500', barColor: '#F97316' };
    }
    if (nameLower.includes('physical') || nameLower.includes('fitness') || nameLower.includes('health') || nameLower.includes('sports')) {
      return { icon: '🤸', bgClass: 'bg-emerald-500', barColor: '#10B981' };
    }
    if (nameLower.includes('socio') || nameLower.includes('emotional') || nameLower.includes('ethical') || nameLower.includes('value') || nameLower.includes('social science') || nameLower.includes('sst') || nameLower.includes('history') || nameLower.includes('civics') || nameLower.includes('geography')) {
      return { icon: '🤝', bgClass: 'bg-purple-500', barColor: '#7C3AED' };
    }
    if (nameLower.includes('cognitive') || nameLower.includes('logical') || nameLower.includes('science') || nameLower.includes('physics') || nameLower.includes('chemistry') || nameLower.includes('biology')) {
      return { icon: '🧠', bgClass: 'bg-indigo-500', barColor: '#6366F1' };
    }
    if (nameLower.includes('art') || nameLower.includes('culture') || nameLower.includes('aesthetic') || nameLower.includes('music') || nameLower.includes('dance') || nameLower.includes('craft')) {
      return { icon: '🎨', bgClass: 'bg-rose-500', barColor: '#F43F5E' };
    }
    if (nameLower.includes('habit') || nameLower.includes('learning') || nameLower.includes('norm')) {
      return { icon: '📚', bgClass: 'bg-sky-500', barColor: '#0EA5E9' };
    }
    if (nameLower.includes('evs') || nameLower.includes('environment') || nameLower.includes('nature') || nameLower.includes('environmental')) {
      return { icon: '🌿', bgClass: 'bg-teal-500', barColor: '#14B8A6' };
    }
    if (nameLower.includes('computer') || nameLower.includes('cs') || nameLower.includes('ict') || nameLower.includes('technology') || nameLower.includes('coding')) {
      return { icon: '💻', bgClass: 'bg-cyan-500', barColor: '#06B6D4' };
    }
    return { icon: '📝', bgClass: 'bg-gray-500', barColor: '#6B7280' };
  };

  // Adjust spacing dynamically depending on the number of items
  const spacingClass = data.length > 5 ? 'space-y-2' : 'space-y-3.5';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col h-[340px]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="text-[11px] font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">Avg Score</span>
      </div>
      <div className={`flex-1 overflow-y-auto ${spacingClass} pr-1 scrollbar-thin`}>
        {data.map((item, i) => {
          const meta = getSubjectMeta(item.subject);
          return (
            <div key={i} className="group flex flex-col space-y-1 transition-all duration-200">
              <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm flex-shrink-0 group-hover:scale-110 transition-transform duration-200">{meta.icon}</span>
                  <span className="truncate group-hover:text-indigo-600 transition-colors" title={item.subject}>
                    {item.subject}
                  </span>
                </div>
                <span className="font-bold tabular-nums ml-2 flex-shrink-0 text-gray-900 group-hover:text-indigo-600 transition-colors">
                  {item.avgScore}%
                </span>
              </div>
              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ease-out ${meta.bgClass}`}
                  style={{ width: `${item.avgScore}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export interface RegionCompetencyPoint {
  regionId: number;
  regionName: string;
  avgCompetency: number;
  topSchoolName: string;
  topSchoolScore: number;
  worstSchoolName: string;
  worstSchoolScore: number;
}

const RegionCompetencyTooltip: React.FC<{ active?: boolean; payload?: any[] }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload as RegionCompetencyPoint | undefined;
  if (!point) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-md min-w-[220px]">
      <p className="text-xs font-semibold text-gray-900 mb-1">{point.regionName}</p>
      <p className="text-xs text-indigo-700 font-medium mb-2">Avg Competency: {point.avgCompetency.toFixed(1)}%</p>
      <div className="space-y-1">
        <p className="text-[11px] text-emerald-700">
          Top school: <span className="font-semibold">{point.topSchoolName}</span> ({point.topSchoolScore.toFixed(1)}%)
        </p>
        <p className="text-[11px] text-red-700">
          Bottom school: <span className="font-semibold">{point.worstSchoolName}</span> ({point.worstSchoolScore.toFixed(1)}%)
        </p>
      </div>
    </div>
  );
};

export const RegionCompetencyLineChart: React.FC<{
  data: RegionCompetencyPoint[];
  onPointClick?: (regionId: number) => void;
}> = ({ data, onPointClick }) => {
  const renderDot = (props: any): React.ReactElement<SVGElement> => {
    const { cx, cy, payload } = props;
    // If positions are not numbers, return a harmless empty SVG group
    if (typeof cx !== 'number' || typeof cy !== 'number') return <g /> as React.ReactElement<SVGElement>;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill="#4F46E5"
        style={onPointClick ? { cursor: 'pointer' } : undefined}
        onClick={() => {
          const regionId = payload?.regionId;
          if (typeof regionId === 'number' && onPointClick) {
            onPointClick(regionId);
          }
        }}
      />
    ) as React.ReactElement<SVGElement>;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Region-wise Avg Competency</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis
            dataKey="regionName"
            tick={{ fontSize: 11 }}
            interval={0}
            angle={-20}
            textAnchor="end"
            height={58}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<RegionCompetencyTooltip />} />
          <Line
            type="monotone"
            dataKey="avgCompetency"
            stroke="#4F46E5"
            strokeWidth={2.5}
            dot={renderDot}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const TopPerformersCard: React.FC<{ items: TopPerformer[] }> = ({ items }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <h3 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-1.5">
      <TrendingUp size={16} /> Top Performers
    </h3>
    {items.length === 0 && <p className="text-xs text-gray-400">No data</p>}
    <div className="space-y-2">
      {items.map(t => (
        <div key={t.id} className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2">
          <span className="text-sm font-medium text-gray-800">{t.name}</span>
          <span className="text-xs text-emerald-700 font-semibold">{t.metric}: {t.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export const AlertsCard: React.FC<{ items: AlertItem[]; onViewAll?: () => void; viewAllLabel?: string }> = ({ items, onViewAll, viewAllLabel = 'View List' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
        <AlertCircle size={16} /> Needs Attention
      </h3>
      {onViewAll && (
        <button
          onClick={onViewAll}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition"
        >
          <List size={14} /> {viewAllLabel}
        </button>
      )}
    </div>
    {items.length === 0 && <p className="text-xs text-gray-400">All clear!</p>}
    <div className="space-y-2">
      {items.map(a => (
        <div key={a.id} className={`flex items-center justify-between rounded-lg px-3 py-2 ${a.severity === 'high' ? 'bg-red-50' : a.severity === 'medium' ? 'bg-amber-50' : 'bg-gray-50'
          }`}>
          <span className="text-sm font-medium text-gray-800">{a.name}</span>
          <span className={`text-xs font-semibold ${a.severity === 'high' ? 'text-red-700' : a.severity === 'medium' ? 'text-amber-700' : 'text-gray-600'
            }`}>{a.issue}</span>
        </div>
      ))}
    </div>
  </div>
);

// ========== Drill-down card for child level items ==========
interface DrillCardProps {
  title: string;
  subtitle?: string;
  metrics: LevelMetrics;
  alerts: AlertItem[];
  navigateTo: string;
}

export const DrillCard: React.FC<DrillCardProps> = ({ title, subtitle, metrics, alerts, navigateTo }) => {
  const navigate = useNavigate();
  const hasHighAlert = alerts.some(a => a.severity === 'high');

  return (
    <div
      onClick={() => navigate(navigateTo)}
      className={`bg-white rounded-xl border p-5 shadow-sm cursor-pointer hover:shadow-md transition group ${hasHighAlert ? 'border-red-200' : 'border-gray-200'
        }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition">{title}</h4>
          {subtitle && <p className="text-[11px] text-gray-400">{subtitle}</p>}
        </div>
        <ChevronRight size={18} className="text-gray-400 group-hover:text-indigo-500 transition" />
      </div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <div>
          <p className="text-base font-bold text-gray-900">{metrics.totalStudents.toLocaleString()}</p>
          <p className="text-[10px] text-gray-400">Students</p>
        </div>
        <div>
          <p className={`text-base font-bold ${metrics.avgCompetency >= 75 ? 'text-indigo-600' : metrics.avgCompetency >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
            {metrics.avgCompetency}%
          </p>
          <p className="text-[10px] text-gray-400">Competency</p>
        </div>
      </div>
      {alerts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {alerts.slice(0, 2).map(a => (
            <p key={a.id} className={`text-[11px] ${a.severity === 'high' ? 'text-red-600' : 'text-amber-600'}`}>
              ⚠ {a.name}: {a.issue}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export const TermComparisonChart: React.FC<{ data: { name: string; 'Term 1': number; 'Term 2': number }[] }> = ({ data }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <h3 className="text-sm font-semibold text-gray-700 mb-3 font-semibold">Term 1 vs Term 2 Competency Comparison</h3>
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} barSize={48} barGap={12}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(value: any) => [`${value}%`, 'Avg Score']} contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13 }} />
        <Bar dataKey="Term 1" fill="#6366F1" radius={[6, 6, 0, 0]} />
        <Bar dataKey="Term 2" fill="#7C3AED" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);
